import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useInboxState } from './useInboxState';

const InboxContext = createContext(null);

function InboxProvider({ children }) {
  const { emails, folderCounts, actions } = useInboxState();

  const [activeFolder, setActiveFolder] = useState('inbox');
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Compose state — { mode: 'new'|'reply'|'replyAll'|'forward', email: obj|null }
  const [compose, setCompose] = useState(null);

  // Domain selection for smart/bundled view
  const [selectedDomain, setSelectedDomain] = useState(null);
  const selectDomain = useCallback((domainKey) => setSelectedDomain(domainKey), []);

  // Filter emails by active folder.
  // When viewing inbox, exclude triaged emails (queued/scheduled/on-board) —
  // they live in Priority Queue / Kanban / Calendar now.
  const folderEmails = useMemo(
    () => emails.filter((e) => {
      if (e.folder !== activeFolder) return false;
      if (activeFolder === 'inbox' && e.queueStatus !== 'none') return false;
      return true;
    }),
    [emails, activeFolder]
  );

  // Selected email object
  const selectedEmail = useMemo(
    () => emails.find((e) => e.id === selectedEmailId) ?? null,
    [emails, selectedEmailId]
  );

  // Select email + mark read (no deselect on re-click — Outlook behavior)
  const selectEmail = useCallback((id) => {
    if (id) {
      setSelectedEmailId(id);
      actions.markRead(id);
    }
  }, [actions]);

  // Toggle bulk selection
  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids) => setSelectedIds(new Set(ids)), []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const startCompose = useCallback(() => setCompose({ mode: 'new', email: null }), []);
  const startReply = useCallback((email, mode = 'reply') => setCompose({ mode, email }), []);
  const closeCompose = useCallback(() => setCompose(null), []);

  const value = useMemo(() => ({
    emails,
    folderEmails,
    activeFolder,
    setActiveFolder,
    selectedEmail,
    selectedEmailId,
    selectEmail,
    selectedIds,
    toggleSelected,
    selectAll,
    clearSelection,
    folderCounts,
    actions,
    compose,
    startCompose,
    startReply,
    closeCompose,
    selectedDomain,
    selectDomain,
  }), [
    emails, folderEmails, activeFolder,
    selectedEmail, selectedEmailId, selectEmail,
    selectedIds, toggleSelected, selectAll, clearSelection,
    folderCounts, actions,
    compose, startCompose, startReply, closeCompose,
    selectedDomain, selectDomain,
  ]);

  return (
    <InboxContext.Provider value={value}>
      {children}
    </InboxContext.Provider>
  );
}

function useInbox() {
  const ctx = useContext(InboxContext);
  if (!ctx) throw new Error('useInbox must be used within <InboxProvider>');
  return ctx;
}

export { InboxProvider, useInbox };
